import { useState, FormEvent, useRef } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    serviceType: '',
    message: ''
  });
  const [uploadedPhotos, setUploadedPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setUploadedPhotos(prev => [...prev, ...newPhotos].slice(0, 10)); // Max 10 photos
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    setUploadedPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('serviceType', formData.serviceType);
      formBody.append('message', formData.message);
      formBody.append('photosUploaded', uploadedPhotos.length > 0 ? `${uploadedPhotos.length} photo(s) uploaded` : 'No photos');

      const response = await fetch('https://readdy.ai/api/form/d64k4fk9vs2s8262n2i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          serviceType: '',
          message: ''
        });
        uploadedPhotos.forEach(photo => URL.revokeObjectURL(photo.preview));
        setUploadedPhotos([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full mb-4">
            GET IN TOUCH
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Get a Free Quote
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Request your free estimate today and discover why we're the most trusted stump grinding service in Atlanta
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            <div className="lg:col-span-3 p-10 lg:p-14">
              <div className="flex items-center gap-2 text-emerald-600 mb-6">
                <i className="ri-checkbox-circle-line text-xl"></i>
                <span className="text-sm font-semibold">Free Quote • No Obligation</span>
              </div>

              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="John Smith"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(555) 123-4567"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Service Needed</label>
                  <select
                    name="serviceType"
                    required
                    value={formData.serviceType}
                    onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">Choose a service...</option>
                    <option value="Standard Grinding">Standard Grinding</option>
                    <option value="Root Pruning">Root Pruning</option>
                    <option value="Premium Cleanup">Premium Cleanup</option>
                    <option value="Emergency Service">Emergency Service</option>
                    <option value="Multiple Stumps">Multiple Stumps</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload Stump Photos
                    <span className="text-gray-500 font-normal ml-1">(optional, up to 10)</span>
                  </label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-6 bg-gray-50 text-gray-600 text-sm rounded-xl border-2 border-dashed border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/30 transition-all cursor-pointer flex flex-col items-center justify-center gap-2"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full">
                      <i className="ri-camera-line text-emerald-600 text-2xl"></i>
                    </div>
                    <p className="font-medium text-gray-700">Click to upload photos</p>
                    <p className="text-xs text-gray-500">JPG, PNG or HEIC • Max 10MB each</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  
                  {uploadedPhotos.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {uploadedPhotos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <div className="w-full aspect-square rounded-lg overflow-hidden border border-gray-200">
                            <img 
                              src={photo.preview} 
                              alt={`Stump photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {uploadedPhotos.length > 0 && (
                    <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill"></i>
                      {uploadedPhotos.length} photo{uploadedPhotos.length > 1 ? 's' : ''} ready to upload
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project Details</label>
                  <textarea
                    name="message"
                    placeholder="Tell us about your stump grinding needs..."
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-emerald-600 text-white text-base font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Get Free Quote
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-xl flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl"></i>
                    <div>
                      <p className="font-semibold">Thank you for contacting us!</p>
                      <p className="text-xs">We'll get back to you within 2 hours.</p>
                    </div>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-3">
                    <i className="ri-error-warning-fill text-2xl"></i>
                    <div>
                      <p className="font-semibold">Oops! Something went wrong.</p>
                      <p className="text-xs">Please try again or call us directly.</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <i className="ri-shield-check-line"></i>
                  Your information is secure and will never be shared with third parties
                </p>
              </form>
            </div>

            <div className="lg:col-span-2 bg-gradient-to-br from-emerald-600 to-emerald-700 p-10 lg:p-14 flex flex-col justify-center">
              <div className="space-y-8">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Why Choose Us?
                  </h3>
                  <p className="text-emerald-100 text-sm leading-relaxed">
                    Committed to quality service and customer satisfaction, we're the trusted choice for professional stump grinding in Atlanta.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Licensed & Insured</h4>
                      <p className="text-emerald-100 text-sm">Fully certified with comprehensive coverage</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Fast Response</h4>
                      <p className="text-emerald-100 text-sm">Quick scheduling available</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-star-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Quality Guaranteed</h4>
                      <p className="text-emerald-100 text-sm">Professional results you can count on</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/20">
                  <p className="text-white font-semibold mb-3">Follow Us</p>
                  <div className="flex items-center gap-3">
                    <a href="https://facebook.com" target="_blank" rel="nofollow" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white hover:text-emerald-600 text-white transition-all cursor-pointer">
                      <i className="ri-facebook-fill text-lg"></i>
                    </a>
                    <a href="https://instagram.com" target="_blank" rel="nofollow" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white hover:text-emerald-600 text-white transition-all cursor-pointer">
                      <i className="ri-instagram-line text-lg"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
